import type { CategoryNode } from '../api/client';

export type CategorySelectionStatus = 'available' | 'selected';
export type CategorySelectionAction = 'selected' | 'removed';

export type CategorySelectionResult<T extends Pick<CategoryNode, 'path'>> = {
  action: CategorySelectionAction;
  items: T[];
};

export type CategoryPickerState<T extends Pick<CategoryNode, 'path'>> = {
  selected: T[];
  query: string;
};

export type CategoryPickerToggleResult<T extends Pick<CategoryNode, 'path'>> = {
  action: CategorySelectionAction;
  selected: T[];
  query: string;
  dropdownOpen: true;
};

export function categorySelectionStatus(
  selected: Pick<CategoryNode, 'path'>[],
  path: string,
): CategorySelectionStatus {
  return selected.some((category) => category.path === path) ? 'selected' : 'available';
}

export function removeCategorySelection<T extends Pick<CategoryNode, 'path'>>(
  selected: T[],
  path: string,
): T[] {
  return selected.filter((category) => category.path !== path);
}

export function toggleCategorySelection<T extends Pick<CategoryNode, 'path'>>(
  selected: T[],
  node: T,
): CategorySelectionResult<T> {
  if (categorySelectionStatus(selected, node.path) === 'selected') {
    return {
      action: 'removed',
      items: removeCategorySelection(selected, node.path),
    };
  }

  return {
    action: 'selected',
    items: [...selected, node],
  };
}

export function toggleCategoryPickerState<T extends Pick<CategoryNode, 'path'>>(
  state: CategoryPickerState<T>,
  node: T,
): CategoryPickerToggleResult<T> {
  const result = toggleCategorySelection(state.selected, node);
  return {
    action: result.action,
    selected: result.items,
    query: state.query,
    dropdownOpen: true,
  };
}
